<template lang="pug">
div
  //- Loading State
  template(v-if="isFetching")
    div(style="max-width: 750px")
      yz-skeleton-loader.mb-4(type="heading")
      yz-skeleton-loader(type="card")
      yz-divider.my-5
      yz-skeleton-loader.mb-4(type="heading")
      yz-skeleton-loader(type="card")

  //- Empty State
  template(v-else-if="!settingsId && !isConfiguring")
    div(style="max-width: 750px")
      yz-empty-state-card(icon="$yzi-settings-02" title="Settings not configured" description="Set creation permissions and branch override controls for your branches." action-label="Set up settings" @action-click="onSetupDefaults")

  //- Content
  template(v-else)
    //- Section 1: Inventory creation permissions
    div(style="max-width: 750px")
      .yz-heading-9.text-neutral-700 Inventory creation permissions
      .yz-regular-body-sm.text-neutral-500.mb-4 Control whether branches are allowed to create their own inventory items.

      yz-card.pa-4(outlined, color="yz-neutral-200" radius="12px")
        yz-radio-group(v-model="creationPermission" :spacing="16")
          yz-radio(value="hq_only")
            template(#label)
              .ml-2
                .yz-medium-body-sm.text-neutral-700 Only HQ can create inventory items
                .yz-regular-body-xs.text-neutral-500.mt-1 Inventory items are created and managed by HQ. Branches can only use items assigned by HQ.
          yz-radio(value="hq_and_branches")
            template(#label)
              .ml-2
                .yz-medium-body-sm.text-neutral-700 HQ and branches can create inventory items
                .yz-regular-body-xs.text-neutral-500.mt-1 HQ-created items are shared across selected branches. Items created by branches are available only within that branch.

    yz-divider.my-5

    //- Section 2: Branch control on HQ products
    div(style="max-width: 750px")
      .yz-heading-9.text-neutral-700 Branch control on HQ products
      .yz-regular-body-sm.text-neutral-500.mb-4 Overrides apply only to the branch and do not change HQ defaults.

      yz-card.pa-4(outlined, color="yz-neutral-200" radius="12px")
        .d-flex.flex-column(style="gap: 12px")
          yz-checkbox(v-model="allowPriceOverride")
            template(#label)
              .ml-2
                .yz-medium-body-sm.text-neutral-700 Allow branches to override price
                .yz-regular-body-xs.text-neutral-500 Branches can override HQ's default pricing and set a local selling price.
          yz-checkbox(v-model="allowSupplierOverride")
            template(#label)
              .ml-2
                .yz-medium-body-sm.text-neutral-700 Allow branches to override supplier
                .yz-regular-body-xs.text-neutral-500 Branches can assign a different supplier based on local sourcing.

</template>

<script>
import { mapGetters, mapActions } from 'vuex'

const SETTINGS_NAME = 'hq.branches.inventory_settings'

export default {
  data: () => ({
    isFetching: true,
    isSaving: false,
    isConfiguring: false,
    settingsId: null,
    creationPermission: null,
    allowPriceOverride: false,
    allowSupplierOverride: false,
    // Store original values to track changes
    originalCreationPermission: null,
    originalAllowPriceOverride: false,
    originalAllowSupplierOverride: false
  }),
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    hasChanges() {
      return this.creationPermission !== this.originalCreationPermission ||
        this.allowPriceOverride !== this.originalAllowPriceOverride ||
        this.allowSupplierOverride !== this.originalAllowSupplierOverride
    }
  },
  watch: {
    hasChanges: {
      handler() {
        this.emitFooterState()
      }
    },
    isSaving: {
      handler() {
        this.emitFooterState()
      }
    },
    isFetching: {
      handler() {
        this.emitFooterState()
      }
    },
    settingsId: {
      handler() {
        this.emitFooterState()
      }
    },
    isConfiguring: {
      handler() {
        this.emitFooterState()
      }
    }
  },
  mounted() {
    this.fetchSettings()
    this.emitFooterState()
  },
  beforeDestroy() {
    this.$emit('update:footer', { show: false })
  },
  methods: {
    ...mapActions(['showMessage']),
    emitFooterState() {
      this.$emit('update:footer', {
        show: !this.isFetching && (!!this.settingsId || this.isConfiguring),
        loading: this.isSaving,
        disabled: !this.hasChanges
      })
    },
    async fetchSettings() {
      this.isFetching = true
      try {
        const response = await this.$axios.get(`/companies/${this.company.id}/settings`, {
          params: { name: SETTINGS_NAME }
        })
        if (response.data.count > 0 && response.data.results && response.data.results.length > 0) {
          const settings = response.data.results[0]
          this.settingsId = settings.id
          this.mapSettingsToData(settings.extra_data)
        }
        this.syncOriginalValues()
      } catch (error) {
        console.error('Error fetching inventory settings:', error)
        this.showMessage(['error', 'Unable to load settings'])
      } finally {
        this.isFetching = false
      }
    },
    mapSettingsToData(extraData) {
      if (!extraData) return
      if (extraData.inventory_creation_permissions) {
        this.creationPermission = extraData.inventory_creation_permissions.mode || 'hq_only'
      }
      if (extraData.branch_product_control_permissions) {
        this.allowPriceOverride = extraData.branch_product_control_permissions.can_override_price || false
        this.allowSupplierOverride = extraData.branch_product_control_permissions.can_override_supplier || false
      }
    },
    syncOriginalValues() {
      this.originalCreationPermission = this.creationPermission
      this.originalAllowPriceOverride = this.allowPriceOverride
      this.originalAllowSupplierOverride = this.allowSupplierOverride
    },
    buildPayload() {
      return {
        name: SETTINGS_NAME,
        extra_data: {
          inventory_creation_permissions: {
            mode: this.creationPermission
          },
          branch_product_control_permissions: {
            can_override_price: this.allowPriceOverride,
            can_override_supplier: this.allowSupplierOverride
          }
        }
      }
    },
    onSetupDefaults() {
      this.creationPermission = 'hq_and_branches'
      this.allowPriceOverride = true
      this.allowSupplierOverride = true
      this.isConfiguring = true
    },
    async saveSettings() {
      if (this.isSaving) return
      this.isSaving = true
      const payload = this.buildPayload()
      try {
        if (this.settingsId) {
          await this.$axios.patch(`/companies/${this.company.id}/settings/${this.settingsId}`, payload)
        } else {
          const response = await this.$axios.post(`/companies/${this.company.id}/settings/`, payload)
          this.settingsId = response.data.id
        }
        this.syncOriginalValues()
        this.showMessage(['success', 'Settings saved'])
      } catch (error) {
        console.error('Error saving inventory settings:', error)
        this.showMessage(['error', 'Unable to save settings'])
      } finally {
        this.isSaving = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
